import { t } from "@lingui/macro";
import { i18n } from "@lingui/core";

import { Currency } from "src/common/currencies";
import { CountryCodeSupported as CountryCodes } from "src/common/country";

export enum UnsupportedCurrency {
  Czk = "CZK",
  Huf = "HUF",
  Nok = "NOK",
  Pln = "PLN",
  Chf = "CHF",
  Hrk = "HRK",
  Ron = "RON",
  Bgn = "BGN",
  Zar = "ZAR",
}

export enum UnsupportedCountry {
  GG = "GG",
  IM = "IM",
  JE = "JE",
}

export const unsupportedCountryCodeToName = (): Record<
  UnsupportedCountry,
  string
> => ({
  [UnsupportedCountry.GG]: i18n._(
    t({
      id: "country_name.guernsey",
      message: "Guernsey",
    })
  ),
  [UnsupportedCountry.IM]: i18n._(
    t({ id: "country_name.isle_of_man", message: "Isle of Man" })
  ),
  [UnsupportedCountry.JE]: i18n._(
    t({ id: "country_name.jersey", message: "Jersey" })
  ),
});

export interface Fields {
  field_name: string;
  local_name: string | null;
  example?: string;
}

export interface BankAccountFieldsByCountryConfig {
  code: CountryCodes | UnsupportedCountry;
  primary_fields: Fields[];
  secondary_fields: Fields[];
  default_currency: Currency | UnsupportedCurrency;
}

export const BankAccountFieldsByCountry: BankAccountFieldsByCountryConfig[] = [
  {
    code: CountryCodes.GB,
    primary_fields: [
      {
        field_name: "branch_code",
        local_name: "Sort code",
      },
      {
        field_name: "account_number",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    default_currency: Currency.Gbp,
  },
  {
    code: UnsupportedCountry.GG,
    primary_fields: [
      {
        field_name: "branch_code",
        local_name: "Sort code",
      },
      {
        field_name: "account_number",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    default_currency: Currency.Gbp,
  },
  {
    code: UnsupportedCountry.IM,
    primary_fields: [
      {
        field_name: "branch_code",
        local_name: "Sort code",
      },
      {
        field_name: "account_number",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    default_currency: Currency.Gbp,
  },
  {
    code: UnsupportedCountry.JE,
    primary_fields: [
      {
        field_name: "branch_code",
        local_name: "Sort code",
      },
      {
        field_name: "account_number",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    default_currency: Currency.Gbp,
  },
  {
    code: CountryCodes.AT,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Bankleitzahl",
      },
      {
        field_name: "account_number",
        local_name: "Kontonummer",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.BE,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "account_number",
        local_name: "Rekeningnummer/Numéro de compte",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.CY,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Kodikos Trapezas",
      },
      {
        field_name: "branch_code",
        local_name: "Kodikos Katastimatos",
      },
      {
        field_name: "account_number",
        local_name: "Arithmos Logariasmou",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.CZ,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [],
    default_currency: UnsupportedCurrency.Czk,
  },
  {
    code: CountryCodes.EE,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "account_number",
        local_name: null,
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.FI,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
      },
      {
        field_name: "account_number",
        local_name: "Tilinumeron rakenne",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.AX,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
        example: "FI21 1234 5600 0007 85",
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
        example: "500015",
      },
      {
        field_name: "account_number",
        local_name: "Tilinumeron rakenne",
        example: "456",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.FR,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
      },
      {
        field_name: "branch_code",
        local_name: "Code Guichet",
      },
      {
        field_name: "account_number",
        local_name: "Numéro de Compte PLUS Clé RIB",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.GF,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
        example: "FR14 2004 1010 0505 0001 3M02 606",
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
        example: "20041",
      },
      {
        field_name: "branch_code",
        local_name: "Code Guichet",
        example: "01005",
      },
      {
        field_name: "account_number",
        local_name: "Numéro de Compte PLUS Clé RIB",
        example: "0500013M026 06",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.GP,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
        example: "FR14 2004 1010 0505 0001 3M02 606",
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
        example: "20041",
      },
      {
        field_name: "branch_code",
        local_name: "Code Guichet",
        example: "01005",
      },
      {
        field_name: "account_number",
        local_name: "Numéro de Compte PLUS Clé RIB",
        example: "0500013M026 06",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.HU,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [],
    default_currency: UnsupportedCurrency.Huf,
  },
  {
    code: CountryCodes.MQ,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
        example: "FR14 2004 1010 0505 0001 3M02 606",
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
        example: "20041",
      },
      {
        field_name: "branch_code",
        local_name: "Code Guichet",
        example: "01005",
      },
      {
        field_name: "account_number",
        local_name: "Numéro de Compte PLUS Clé RIB",
        example: "0500013M026 06",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.YT,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
        example: "FR14 2004 1010 0505 0001 3M02 606",
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
        example: "20041",
      },
      {
        field_name: "branch_code",
        local_name: "Code Guichet",
        example: "01005",
      },
      {
        field_name: "account_number",
        local_name: "Numéro de Compte PLUS Clé RIB",
        example: "0500013M026 06",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.RE,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
        example: "FR14 2004 1010 0505 0001 3M02 606",
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
        example: "20041",
      },
      {
        field_name: "branch_code",
        local_name: "Code Guichet",
        example: "01005",
      },
      {
        field_name: "account_number",
        local_name: "Numéro de Compte PLUS Clé RIB",
        example: "0500013M026 06",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.BL,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
        example: "FR14 2004 1010 0505 0001 3M02 606",
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
        example: "20041",
      },
      {
        field_name: "branch_code",
        local_name: "Code Guichet",
        example: "01005",
      },
      {
        field_name: "account_number",
        local_name: "Numéro de Compte PLUS Clé RIB",
        example: "0500013M026 06",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.MF,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
        example: "FR14 2004 1010 0505 0001 3M02 606",
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
        example: "20041",
      },
      {
        field_name: "branch_code",
        local_name: "Code Guichet",
        example: "01005",
      },
      {
        field_name: "account_number",
        local_name: "Numéro de Compte PLUS Clé RIB",
        example: "0500013M026 06",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.PM,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
        example: "FR14 2004 1010 0505 0001 3M02 606",
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
        example: "20041",
      },
      {
        field_name: "branch_code",
        local_name: "Code Guichet",
        example: "01005",
      },
      {
        field_name: "account_number",
        local_name: "Numéro de Compte PLUS Clé RIB",
        example: "0500013M026 06",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.DE,
    primary_fields: [
      {
        field_name: "bank_code",
        local_name: "Bankleitzahl",
      },
      {
        field_name: "account_number",
        local_name: "Kontonummer",
      },
    ],
    secondary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.GR,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Kodikos Trapezas",
      },
      {
        field_name: "branch_code",
        local_name: "Kodikos Katastimatos",
      },
      {
        field_name: "account_number",
        local_name: "Arithmos Logariasmou",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.IE,
    primary_fields: [
      {
        field_name: "branch_code",
        local_name: "Sort code",
      },
      {
        field_name: "account_number",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.IT,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Codice ABI",
      },
      {
        field_name: "branch_code",
        local_name: "CAB",
      },
      {
        field_name: "account_number",
        local_name: "Numero di conto",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.LV,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: null,
      },
      {
        field_name: "account_number",
        local_name: null,
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.LT,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: null,
      },
      {
        field_name: "account_number",
        local_name: null,
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.LU,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: null,
      },
      {
        field_name: "account_number",
        local_name: null,
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.MT,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "branch_code",
        local_name: "Sort code",
      },
      {
        field_name: "account_number",
        local_name: "Numru tal‑kont bankarju",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.MC,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Code Banque",
      },
      {
        field_name: "branch_code",
        local_name: "Code Guichet",
      },
      {
        field_name: "account_number",
        local_name: "Numéro de Compte PLUS Clé RIB",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.NL,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: null,
      },
      {
        field_name: "account_number",
        local_name: "Rekeningnummer",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.NO,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Bankkode",
      },
      {
        field_name: "account_number",
        local_name: "Kontonummer",
      },
    ],
    default_currency: UnsupportedCurrency.Nok,
  },
  {
    code: CountryCodes.PT,
    primary_fields: [
      {
        field_name: "bank_code",
        local_name: "Código de Banco",
      },
      {
        field_name: "branch_code",
        local_name: "Código de Balcão",
      },
      {
        field_name: "account_number",
        local_name: "Número de conta e dígitos de controlo",
      },
    ],
    secondary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.PL,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [],
    default_currency: UnsupportedCurrency.Pln,
  },
  {
    code: CountryCodes.SM,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [
      {
        field_name: "bank_code",
        local_name: "Codice ABI",
      },
      {
        field_name: "branch_code",
        local_name: "CAB",
      },
      {
        field_name: "account_number",
        local_name: "Numero di conto",
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.SK,
    primary_fields: [
      {
        field_name: "bank_code",
        local_name: "Kód banky",
      },
      {
        field_name: "account_number",
        local_name: "Předčíslí, A Číslo účtu",
      },
    ],
    secondary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.SI,
    primary_fields: [
      {
        field_name: "iban",
        local_name: "TRR",
      },
    ],
    secondary_fields: [],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.ES,
    primary_fields: [
      {
        field_name: "bank_code",
        local_name: "Banco",
      },
      {
        field_name: "branch_code",
        local_name: "Código de sucursal",
      },
      {
        field_name: "account_number",
        local_name: "Número de cuenta Y Dígitos de control",
      },
    ],
    secondary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    default_currency: Currency.Eur,
  },
  {
    code: CountryCodes.SE,
    primary_fields: [
      {
        field_name: "branch_code",
        local_name: "Clearingnummer",
      },
      {
        field_name: "account_number",
        local_name: "Kontonummer",
      },
    ],
    secondary_fields: [],
    default_currency: Currency.Sek,
  },
  {
    code: CountryCodes.DK,
    primary_fields: [
      {
        field_name: "account_number",
        local_name: "Kontonummer",
      },
      {
        field_name: "bank_code",
        local_name: "Registreringsnummer",
      },
    ],
    secondary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    default_currency: Currency.Dkk,
  },
  {
    code: CountryCodes.AU,
    primary_fields: [
      {
        field_name: "account_number",
        local_name: null,
      },
      {
        field_name: "branch_code",
        local_name: "BSB",
      },
    ],
    secondary_fields: [],
    default_currency: Currency.Aud,
  },
  {
    code: CountryCodes.CA,
    primary_fields: [
      {
        field_name: "bank_code",
        local_name: "Institution number",
      },
      {
        field_name: "branch_code",
        local_name: "Transit number",
      },
      {
        field_name: "account_number",
        local_name: "Account number",
      },
    ],
    secondary_fields: [],
    default_currency: Currency.Cad,
  },
  {
    code: CountryCodes.NZ,
    primary_fields: [
      {
        field_name: "bank_code",
        local_name: "Bank number",
      },
      {
        field_name: "branch_code",
        local_name: "Branch number",
      },
      {
        field_name: "account_number",
        local_name: "Account number",
      },
      {
        field_name: "account_suffix",
        local_name: "Suffix",
      },
    ],
    secondary_fields: [],
    default_currency: Currency.Nzd,
  },
  {
    code: CountryCodes.CH,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [],
    default_currency: UnsupportedCurrency.Chf,
  },
  {
    code: CountryCodes.HR,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [],
    default_currency: UnsupportedCurrency.Hrk,
  },
  {
    code: CountryCodes.RO,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [],
    default_currency: UnsupportedCurrency.Ron,
  },
  {
    code: CountryCodes.BG,
    primary_fields: [
      {
        field_name: "iban",
        local_name: null,
      },
    ],
    secondary_fields: [],
    default_currency: UnsupportedCurrency.Bgn,
  },
  {
    code: CountryCodes.US,
    primary_fields: [
      {
        field_name: "bank_code",
        local_name: "Routing number",
      },
      {
        field_name: "account_number",
        local_name: "Account number",
      },
      {
        field_name: "account_type",
        local_name: "Account type",
      },
    ],
    secondary_fields: [],
    default_currency: Currency.Usd,
  },
  {
    code: CountryCodes.ZA,
    primary_fields: [
      {
        field_name: "branch_code",
        local_name: "Branch number",
      },
      {
        field_name: "account_number",
        local_name: "Account number",
      },
    ],
    secondary_fields: [],
    default_currency: UnsupportedCurrency.Zar,
  },
];
